// User utility functions for the academic learning platform
// Handles user display, privacy, and avatar generation

const animalEmojis = [
  "🐶",
  "🐱",
  "🐭",
  "🐹",
  "🐰",
  "🦊",
  "🐻",
  "🐼",
  "🐨",
  "🐯",
  "🦁",
  "🐮",
  "🐷",
  "🐸",
  "🐵",
  "🐔",
  "🐧",
  "🐦",
  "🐤",
  "🐣",
  "🐺",
  "🐗",
  "🐴",
  "🦄",
  "🐝",
  "🐛",
  "🦋",
  "🐌",
  "🐞",
  "🐜",
  "🦗",
  "🕷️",
  "🐢",
  "🐍",
  "🦎",
  "🦖",
  "🦕",
  "🐙",
  "🦑",
  "🦐",
  "🦀",
  "🐡",
  "🐠",
  "🐟",
  "🐬",
  "🐳",
  "🐋",
  "🦈",
];

/**
 * Generate a consistent animal avatar emoji based on username
 * @param {string} username - The username to generate avatar for
 * @returns {string} - Animal emoji
 */
export const getAnimalAvatar = (username) => {
  if (!username) return animalEmojis[0];

  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    const char = username.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const index = Math.abs(hash) % animalEmojis.length;
  return animalEmojis[index];
};

/**
 * Generate a display name for a user based on privacy settings
 * @param {string} username - The original username
 * @param {string} displayType - Type of display: "student_id", "short_student_id", "friendly", "masked"
 * @returns {string} - Formatted display name
 */
export const getDisplayUsername = (username, displayType = "friendly") => {
  if (!username) return "ไม่ทราบชื่อผู้ใช้";

  switch (displayType) {
    case "student_id":
      // Show as student ID format: stu_xxxxx
      if (username.includes("smoker")) {
        return `stu_${username.slice(-4)}`;
      }
      return `stu_${username.slice(0, 5)}`;

    case "short_student_id":
      // Show shortened student ID: s_xxx
      if (username.includes("smoker")) {
        return `s_${username.slice(-3)}`;
      }
      return `s_${username.slice(0, 3)}`;

    case "friendly":
      // Show friendly name with partial masking
      if (username.length <= 3) {
        return username;
      }
      const firstChar = username.charAt(0);
      const lastChar = username.charAt(username.length - 1);
      const maskedMiddle = "*".repeat(Math.max(1, username.length - 2));
      return `${firstChar}${maskedMiddle}${lastChar}`;

    case "masked":
      // Heavy masking for privacy
      if (username.length <= 2) {
        return "*".repeat(username.length);
      }
      const firstTwoChars = username.slice(0, 2);
      const maskedRest = "*".repeat(username.length - 2);
      return `${firstTwoChars}${maskedRest}`;

    case "anonymous":
      // Complete anonymization
      return "นักเรียน";

    default:
      return username;
  }
};

/**
 * Generate a masked email for privacy
 * @param {string} email - The email to mask
 * @returns {string} - Masked email
 */
export const getMaskedEmail = (email) => {
  if (!email || !email.includes("@")) return "****@****.***";

  const [localPart, domain] = email.split("@");
  const maskedLocal =
    localPart.length > 2
      ? `${localPart.slice(0, 2)}${"*".repeat(localPart.length - 2)}`
      : "*".repeat(localPart.length);

  const [domainName, domainExt] = domain.split(".");
  const maskedDomain =
    domainName.length > 2
      ? `${domainName.slice(0, 2)}${"*".repeat(domainName.length - 2)}`
      : "*".repeat(domainName.length);

  return `${maskedLocal}@${maskedDomain}.${domainExt}`;
};

/**
 * Generate initials from a username
 * @param {string} username - The username
 * @returns {string} - Initials (2 characters)
 */
export const getUserInitials = (username) => {
  if (!username) return "??";

  if (username.length === 1) {
    return username.toUpperCase();
  }

  return `${username.charAt(0)}${username.charAt(
    username.length - 1
  )}`.toUpperCase();
};

/**
 * Check if a username should have admin privileges
 * @param {string} username - The username to check
 * @returns {boolean} - Whether user has admin privileges
 */
export const isAdminUser = (username) => {
  return username === "smoker8888";
};

/**
 * Generate a color based on username for consistent theming
 * @param {string} username - The username
 * @returns {string} - Hex color code
 */
export const getUserColor = (username) => {
  if (!username) return "#666666";

  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    const char = username.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  // Generate a color that's not too light or too dark
  const colors = [
    "#1976d2",
    "#388e3c",
    "#f57c00",
    "#d32f2f",
    "#7b1fa2",
    "#303f9f",
    "#0288d1",
    "#0097a7",
    "#00796b",
    "#689f38",
    "#afb42b",
    "#fbc02d",
    "#ff8f00",
    "#f57c00",
    "#ff5722",
  ];

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Format a username for display in different contexts
 * @param {string} username - The original username
 * @param {object} options - Display options
 * @returns {object} - Object with various formatted versions
 */
export const formatUserDisplay = (username, options = {}) => {
  const {
    showAvatar = true,
    showColor = true,
    displayType = "friendly",
  } = options;

  return {
    original: username,
    display: getDisplayUsername(username, displayType),
    avatar: showAvatar ? getAnimalAvatar(username) : null,
    initials: getUserInitials(username),
    color: showColor ? getUserColor(username) : null,
    isAdmin: isAdminUser(username),
  };
};
